import React, { useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Button from '../../ui/Button';
import { formatWhatsApp, isValidEmail } from '../../../utils/formatUtils';

// Esquema de validação com Yup
const validationSchema = Yup.object({
  name: Yup.string().required('O nome é obrigatório'),
  businessName: Yup.string().required('O nome do negócio é obrigatório'),
  phone: Yup.string().nullable(),
  email: Yup.string()
    .required('O email é obrigatório')
    .email('Email inválido'),
  password: Yup.string()
    .required('A senha é obrigatória')
    .min(6, 'A senha deve ter pelo menos 6 caracteres'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'As senhas devem coincidir')
    .required('Confirmação de senha é obrigatória')
});

const PersonalInfoStep = ({ onNext, initialData = {} }) => {
  console.log('[DEBUG-STEP1] Renderizando PersonalInfoStep');
  console.log('[DEBUG-STEP1] Dados iniciais recebidos:', {
    temNome: !!initialData.name,
    temEmail: !!initialData.email,
    temSenha: !!initialData.password,
    senhaMascarada: initialData.password === '********',
    temBusinessName: !!initialData.businessName,
    temPhone: !!initialData.phone
  });

  // Refs para armazenar os valores atuais do formulário para logs
  const formValuesRef = useRef(null);
  const formErrorsRef = useRef(null);

  // Log quando o componente é montado
  useEffect(() => {
    console.log('[DEBUG-STEP1] PersonalInfoStep montado ou atualizado');
    
    // Verificar problema da senha mascarada
    if (initialData.password === '********') {
      console.warn('[DEBUG-STEP1] ALERTA: Senha mascarada no initialData. Isso impedirá o registro correto!');
    }
    
    return () => {
      console.log('[DEBUG-STEP1] PersonalInfoStep desmontado');
    };
  }, [initialData]);

  // Efeito para monitorar mudanças nos valores do formulário
  useEffect(() => {
    // Verificar se há valores para logar
    if (formValuesRef.current) {
      const values = formValuesRef.current;
      const errors = formErrorsRef.current || {};
      
      console.log('[DEBUG-STEP1] Valores do formulário atualizados:', {
        temSenha: !!values.password,
        senhaTamanho: values.password ? values.password.length : 0,
        temConfirmacaoSenha: !!values.confirmPassword,
        senhasIguais: values.password === values.confirmPassword
      });
      
      // Monitorar possíveis problemas com a senha
      if (errors.password) {
        console.warn('[DEBUG-STEP1] Erro de validação na senha:', errors.password);
      }
      if (errors.confirmPassword) {
        console.warn('[DEBUG-STEP1] Erro na confirmação de senha:', errors.confirmPassword);
      }
    }
  }, [formValuesRef.current, formErrorsRef.current]);

  // Manipular a mudança do campo de telefone
  const handlePhoneChange = (e, setFieldValue) => {
    const formattedPhone = formatWhatsApp(e.target.value);
    setFieldValue('phone', formattedPhone);
  };
  
  // Valores iniciais do formulário, com ajuste para senha mascarada
  const initialValues = {
    name: initialData.name || '',
    businessName: initialData.businessName || '',
    phone: initialData.phone || '',
    email: initialData.email || '',
    // Se a senha estiver mascarada, não a utilize como valor inicial
    password: initialData.password === '********' ? '' : (initialData.password || ''),
    confirmPassword: initialData.password === '********' ? '' : (initialData.password || '')
  };
  
  // Enviar o formulário para o próximo passo
  const handleSubmit = (values, { setSubmitting }) => {
    console.log('[DEBUG-STEP1] Formulário enviado com os valores:', {
      temNome: !!values.name,
      temEmail: !!values.email,
      emailValido: isValidEmail(values.email),
      temSenha: !!values.password,
      senhaTamanho: values.password.length
    });
    
    // Verificação final de segurança
    if (!values.password || values.password.length < 6) {
      console.error('[DEBUG-STEP1] ERRO: Tentando enviar formulário com senha inválida');
      setSubmitting(false);
      return;
    }
    
    // Removendo a confirmação de senha antes de passar os dados adiante
    const { confirmPassword, ...userData } = values;
    
    console.log('[DEBUG-STEP1] Dados sendo enviados para o próximo passo:', {
      campos: Object.keys(userData),
      temSenha: !!userData.password,
      email: userData.email
    });
    
    // Verificação adicional de segurança para a senha
    if (userData.password === '********') {
      console.error('[DEBUG-STEP1] ERRO CRÍTICO: Senha mascarada sendo enviada ao próximo passo!');
    }
    
    onNext(userData);
  };

  // Função auxiliar para atualizar as refs com os valores atuais
  const updateFormRefs = (values, errors) => {
    formValuesRef.current = values;
    formErrorsRef.current = errors;
  };
  
  return (
    <div className="animate-fadeIn">
      <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">
        Vamos começar seu cadastro
      </h2>
      <p className="text-gray-600 text-center mb-8">
        Preencha os dados iniciais para criar sua conta
      </p>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, setFieldValue, values, handleChange }) => {
          // Atualizar refs sempre que o Formik re-renderizar
          updateFormRefs(values, errors);
          
          return (
            <Form className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Seu nome completo
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name && touched.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Digite seu nome completo"
                  onChange={(e) => {
                    handleChange(e);
                    console.log('[DEBUG-STEP1] Nome alterado:', e.target.value);
                  }}
                />
                <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600" />
              </div>

              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do seu negócio
                </label>
                <Field
                  type="text"
                  id="businessName"
                  name="businessName"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.businessName && touched.businessName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Digite o nome do seu salão ou negócio"
                  onChange={(e) => {
                    handleChange(e);
                    console.log('[DEBUG-STEP1] Nome do negócio alterado:', e.target.value);
                  }}
                />
                <ErrorMessage name="businessName" component="div" className="mt-1 text-sm text-red-600" />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone (com WhatsApp)
                </label>
                <Field
                  type="text"
                  id="phone"
                  name="phone"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phone && touched.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="(00) 00000-0000"
                  onChange={(e) => handlePhoneChange(e, setFieldValue)}
                />
                <ErrorMessage name="phone" component="div" className="mt-1 text-sm text-red-600" />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email && touched.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Digite seu email"
                  inputMode="email"
                  onChange={(e) => {
                    handleChange(e);
                    console.log('[DEBUG-STEP1] Email alterado:', e.target.value);
                  }}
                />
                <ErrorMessage name="email" component="div" className="mt-1 text-sm text-red-600" />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <Field
                  type="password"
                  id="password"
                  name="password"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password && touched.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Crie uma senha forte"
                  onChange={(e) => {
                    handleChange(e);
                    console.log('[DEBUG-STEP1] Senha alterada:', e.target.value ? `${e.target.value.length} caracteres` : 'vazia');
                  }}
                />
                <ErrorMessage name="password" component="div" className="mt-1 text-sm text-red-600" />
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar senha
                </label>
                <Field
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword && touched.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Digite novamente sua senha"
                  onChange={(e) => {
                    handleChange(e);
                    console.log('[DEBUG-STEP1] Confirmação de senha alterada:', 
                      e.target.value === values.password ? 'Coincide' : 'Não coincide');
                  }}
                />
                <ErrorMessage name="confirmPassword" component="div" className="mt-1 text-sm text-red-600" />
              </div>
              
              <div className="pt-6">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  fullWidth
                  onClick={() => {
                    console.log('[DEBUG-STEP1] Botão de submit clicado. Estado do formulário:', {
                      valido: Object.keys(errors).length === 0,
                      erros: Object.keys(errors),
                      temSenha: !!values.password,
                      senhaTamanho: values.password?.length
                    });
                  }}
                >
                  Continuar
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PersonalInfoStep; 